import Vue from 'vue';
import firebase from '@/plugins/firebase';
import { database } from '@/plugins/firebase';
import store from "@/store";
import _ from "lodash";
import { logActivity } from '@/services/ActivityLogService';
const PricingCollection = 'pricing';
export const PricingModule = {
    namespaced: true,
    state: [],
    mutations: {
        store(state, pricing) {
            const x = state.find(x => x.id === pricing.id);
            if (x) {
                if (_.isEqual(x, pricing)) {
                    return;
                }
                state.splice(state.indexOf(x), 1, pricing);
                store.dispatch('pricing/storeCache');
                return;
            }
            state.push(pricing);
            store.dispatch('pricing/storeCache');
        },
        update(state, pricing) {
            state.find((oldPricing, index) => {
                if (oldPricing.id === pricing.id) {
                    Vue.set(state, index, pricing);
                }
            });
        },
        destroy(state, pricing) {
            const existingPricing = state.find((item) => pricing.id === item.id);
            if (existingPricing) {
                existingPricing.data.deletedAt = pricing.data.deletedAt;
            }
        },
        clear(state) {
            state.splice(0, state.length);
        },
    },
    actions: {
        async store(context, pricing) {
            // Validate effectiveDate - mandatory field
            const { validateEffectiveDateOrThrow } = await import('@/utils/effectiveDateValidation');
            await validateEffectiveDateOrThrow(pricing.data.effectiveDate);
            await database.collection(PricingCollection).add(pricing.data);
            // Log activity (fire-and-forget)
            logActivity('CREATE', 'pricing', pricing.id || 'new', null, pricing.data);
        },
        async update(context, pricing) {
            // Validate effectiveDate - mandatory field
            const { validateEffectiveDateOrThrow } = await import('@/utils/effectiveDateValidation');
            await validateEffectiveDateOrThrow(pricing.data.effectiveDate);
            let document = await database
                .collection(PricingCollection)
                .doc(pricing.id);
            await document.update(pricing.data);
            // Log activity (fire-and-forget)
            logActivity('UPDATE', 'pricing', pricing.id, null, pricing.data);
        },
        async destroy(context, pricing) {
            // Validate delete is allowed (effectiveDate must be after last concluded work)
            const { validateDeleteOrThrow } = await import('@/utils/effectiveDateValidation');
            await validateDeleteOrThrow(pricing.data.effectiveDate);
            // Log activity (fire-and-forget)
            logActivity('DELETE', 'pricing', pricing.id, pricing.data, null);
            // Soft delete - set timestamp
            pricing.data.deletedAt = firebase.firestore.Timestamp.now();
            // Update document (not delete)
            const doc = database.collection(PricingCollection).doc(pricing.id);
            await doc.update({ deletedAt: pricing.data.deletedAt });
            // Update local state and cache
            context.commit('destroy', pricing);
            context.dispatch('storeCache');
        },
        async listen(context) {
            context.commit('clear');
            //await context.dispatch('loadCache');
            return database
                .collection(PricingCollection)
                .onSnapshot((snapshot) => {
                for (let change of snapshot.docChanges()) {
                    switch (change.type) {
                        case 'added':
                            context.commit('store', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            break;
                        case 'modified':
                            context.commit('update', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            context.dispatch('storeCache');
                            break;
                        case 'removed':
                            context.commit('destroy', {
                                id: change.doc.id,
                                data: change.doc.data(),
                            });
                            context.dispatch('storeCache');
                            break;
                    }
                }
            });
        },
        async storeCache(context) {
            localStorage.removeItem(PricingCollection);
            try {
                localStorage.setItem(PricingCollection, JSON.stringify(context.state));
            }
            catch (e) { /* localStorage quota exceeded or unavailable - non-critical */ }
        },
        async loadCache(context) {
            const cache = localStorage.getItem(PricingCollection);
            if (!cache) {
                return;
            }
            const pricing = JSON.parse(cache);
            for (const price of pricing) {
                context.commit('store', { id: price.id, data: price.data });
            }
        },
    },
    getters: {
        // Active entities only (for selection dropdowns) - deletedAt is null, undefined, or missing
        active(state) {
            return state.filter(p => !p.data.deletedAt);
        },
        // All entities including deleted (for historical views)
        all(state) {
            return state;
        },
        // Deleted entities only (for admin filter)
        deleted(state) {
            return state.filter(p => !!p.data.deletedAt);
        },
        // Active first, then deleted (for management list per FR-010)
        allSortedForManagement(state) {
            const active = state.filter(p => !p.data.deletedAt);
            const deleted = state.filter(p => !!p.data.deletedAt);
            return [...active, ...deleted];
        },
    },
};
